// The Settings form (spec §5.6, D5, D12, D13): what each setting is, how a
// value the API sends reads in the form, and the PATCH a form makes — only
// what changed (the API writes one SettingsChange per changed setting).

import { formatDay } from "./format";
import { displayName } from "../../tasks/userDisplay";

/**
 * D5's caps, checked on a target's weights. A breach doesn't block a
 * target: it needs an override reason.
 */
export const CAP_FIELDS = [
  {
    key: "max_holdings",
    label: "Most holdings",
    unit: "holdings",
    kind: "int",
    help: "A target with more holdings than this is a breach. Default 20.",
  },
  {
    key: "add_min_pct",
    label: "A new holding, at least",
    unit: "%",
    kind: "pct",
    help: "A holding added at a smaller weight is a breach. Default 3%.",
  },
  {
    key: "add_max_pct",
    label: "A new holding, at most",
    unit: "%",
    kind: "pct",
    help: "A holding added at a larger weight is a breach. Default 8%.",
  },
  {
    key: "trim_above_pct",
    label: "Trim above",
    unit: "%",
    kind: "pct",
    help: "A holding weighing more than this is a breach — trim it, or give a reason. Default 12%.",
  },
  {
    key: "min_cash_pct",
    label: "Cash, at least",
    unit: "%",
    kind: "pct",
    help: "Cash below this share of the account is a breach. Default 5%.",
  },
  {
    key: "max_thesis_pct",
    label: "One primary thesis, at most",
    unit: "%",
    kind: "pct",
    help: "The holdings behind one primary thesis, together, above this are a breach. Default 35%.",
  },
  {
    key: "max_sector_pct",
    label: "One sector, at most",
    unit: "%",
    kind: "pct",
    help: "The holdings in one sector, together, above this are a breach. Default 40%.",
  },
];

/** The other settings, by key (labels for the form and the history). */
export const SETTING_LABELS = {
  drift_band_pct: "Drift band",
  min_trade_usd: "Minimum trade",
  executor: "Executor",
  tax_rate_short: "Short-term tax rate",
  tax_rate_long: "Long-term tax rate",
  benchmark_symbol: "Benchmark",
  inception: "Inception",
};

const CAP_LABELS = Object.fromEntries(CAP_FIELDS.map((f) => [`caps.${f.key}`, f.label]));

/** A history row's field ("caps.add_min_pct", "drift_band_pct") in words. */
export function settingLabel(field) {
  return CAP_LABELS[field] ?? SETTING_LABELS[field] ?? field;
}

/** "3.00" → "3", "12.50" → "12.5": a decimal string without its trailing zeros. */
export function trimNumber(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const value = Number(raw);
  return Number.isFinite(value) ? String(value) : String(raw);
}

/** A tax rate the API sends ("0.3200", a fraction) as a percentage for the form ("32"). */
export function rateToPercent(rate) {
  if (rate === null || rate === undefined || rate === "") return "";
  const value = Number(rate);
  return Number.isFinite(value) ? String(Math.round(value * 10000) / 100) : String(rate);
}

/** A percentage from the form ("32.5") as the fraction the API takes ("0.3250"). */
export function percentToRate(percent) {
  // At most two decimals in (formErrors), four out: toFixed absorbs the
  // float error (14.35 / 100 is 0.14350000000000002).
  return (Number(String(percent).trim()) / 100).toFixed(4);
}

/** The form's values (all strings) from the settings the API sent. */
export function formFromSettings(settings) {
  const caps = settings?.caps ?? {};
  return {
    caps: Object.fromEntries(CAP_FIELDS.map((f) => [f.key, trimNumber(caps[f.key])])),
    drift_band_pct: trimNumber(settings?.drift_band_pct),
    min_trade_usd: trimNumber(settings?.min_trade_usd),
    executor: settings?.executor ? String(settings.executor) : "",
    tax_rate_short: rateToPercent(settings?.tax_rate_short),
    tax_rate_long: rateToPercent(settings?.tax_rate_long),
    benchmark_symbol: settings?.benchmark_symbol ?? "",
    inception: settings?.inception ?? "",
  };
}

const PCT = /^\d{1,3}(\.\d{1,2})?$/;
const INT = /^\d{1,3}$/;
const MONEY = /^\d{1,6}(\.\d{1,2})?$/;
const SYMBOL = /^[A-Z][A-Z0-9.-]{0,9}$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

const PCT_HINT = "Use a number like 8 or 8.5 — at most two decimals.";

/**
 * What's wrong with the form's shape, by field (`caps.<key>` for a cap):
 * not a number, more decimals than the API keeps, a benchmark that isn't a
 * ticker. Ranges are the server's to judge; its answer lands on the field.
 */
export function formErrors(form) {
  const errors = {};
  for (const field of CAP_FIELDS) {
    const value = form.caps[field.key].trim();
    if (!(field.kind === "int" ? INT : PCT).test(value)) {
      errors[`caps.${field.key}`] = field.kind === "int" ? "Use a whole number, like 20." : PCT_HINT;
    }
  }
  if (!PCT.test(form.drift_band_pct.trim())) errors.drift_band_pct = "Use points like 2 or 2.5 — at most two decimals.";
  if (!MONEY.test(form.min_trade_usd.trim())) errors.min_trade_usd = "Use dollars like 25 or 25.50.";
  for (const key of ["tax_rate_short", "tax_rate_long"]) {
    const value = form[key].trim();
    if (!PCT.test(value) || Number(value) > 100) errors[key] = "Use a percentage from 0 to 100, like 32 or 32.5.";
  }
  if (!SYMBOL.test(form.benchmark_symbol.trim().toUpperCase())) {
    errors.benchmark_symbol = "Use a ticker like VTI.";
  }
  if (form.inception && !DAY.test(form.inception)) errors.inception = "Use a date, or leave it empty.";
  return errors;
}

/**
 * The PATCH body for what changed from `settings` (without the reason),
 * and the changed settings' names. Values are compared as numbers, so
 * "8" against "8.00" is no change; locked settings are never sent.
 */
export function patchFromForm(form, settings) {
  const body = {};
  const changed = [];
  const locked = new Set(settings?.locked ?? []);
  const caps = {};
  for (const field of CAP_FIELDS) {
    const value = form.caps[field.key].trim();
    if (Number(value) !== Number(settings.caps[field.key])) {
      caps[field.key] = field.kind === "int" ? Number(value) : value;
      changed.push(field.label);
    }
  }
  if (Object.keys(caps).length) body.caps = caps;
  for (const key of ["drift_band_pct", "min_trade_usd"]) {
    const value = form[key].trim();
    if (Number(value) !== Number(settings[key])) {
      body[key] = value;
      changed.push(SETTING_LABELS[key]);
    }
  }
  const executor = form.executor ? Number(form.executor) : null;
  if (executor !== (settings.executor ?? null)) {
    body.executor = executor;
    changed.push(SETTING_LABELS.executor);
  }
  for (const key of ["tax_rate_short", "tax_rate_long"]) {
    const value = percentToRate(form[key]);
    if (Number(value) !== Number(settings[key])) {
      body[key] = value;
      changed.push(SETTING_LABELS[key]);
    }
  }
  const benchmark = form.benchmark_symbol.trim().toUpperCase();
  if (!locked.has("benchmark_symbol") && benchmark !== settings.benchmark_symbol) {
    body.benchmark_symbol = benchmark;
    changed.push(SETTING_LABELS.benchmark_symbol);
  }
  const inception = form.inception || null;
  if (!locked.has("inception") && inception !== (settings.inception ?? null)) {
    body.inception = inception;
    changed.push(SETTING_LABELS.inception);
  }
  return { body, changed };
}

/** A history value (as the change stored it) in words: "8%", "2 points", "$25", "32%", an agent's name. */
export function settingValue(field, value) {
  if (value === null || value === undefined || value === "") {
    return field === "executor" ? "nobody" : "not set";
  }
  if (field === "executor") return value?.username == null ? `user #${value?.id}` : displayName(value);
  if (field === "caps.max_holdings") return `${value} holdings`;
  if (field.startsWith("caps.")) return `${trimNumber(value)}%`;
  if (field === "drift_band_pct") return `${trimNumber(value)} points`;
  if (field === "min_trade_usd") return `$${trimNumber(value)}`;
  if (field === "tax_rate_short" || field === "tax_rate_long") return `${rateToPercent(value)}%`;
  if (field === "inception") return formatDay(`${value}T12:00:00Z`, "UTC") ?? String(value);
  return String(value);
}

/** The named executor's name: "User #31" when the server didn't say, "unnamed account #31" when it renders blank (#38). */
export function executorName(settings) {
  const { executor: id, executor_username: username } = settings;
  return username == null ? `User #${id}` : displayName({ id, username });
}

const CANT_ACT = "nobody can record the account's snapshots, make sync plans or record fills";

/**
 * Why the named executor can't act, in a sentence — null when it can
 * (`executor_valid`, the server's own answer). The reason comes from the
 * agents list when it has the agent: deactivated, or now another person's.
 */
export function executorProblem(settings, agents, me) {
  if (!settings || settings.executor_valid) return null;
  if (settings.executor === null || settings.executor === undefined) {
    return `Nobody is named, so ${CANT_ACT}. Name one of your agents.`;
  }
  const name = executorName(settings);
  const agent = (agents ?? []).find((a) => a.id === settings.executor);
  let why = "it was deactivated or now works for someone else";
  if (agent && !agent.is_active) why = "it was deactivated";
  else if (agent && agent.owner?.id !== me?.id) {
    why = agent.owner ? `it now works for ${displayName(agent.owner)}` : "it no longer works for anyone";
  }
  return `${name} can no longer act: ${why}. Until you name another, ${CANT_ACT}.`;
}
